"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import { Marker } from "@react-google-maps/api"
import type { TowerWithCoords } from "../../../types/project"
import type { google } from "../../../services/googleMaps"
import { debugLog } from "../../../constants/debug"

interface TowerMarkerProps {
  tower: TowerWithCoords
  angle: number
  currentZoom: number
  onMouseOver: (tower: TowerWithCoords) => void
  onMouseOut: () => void
  onClick: (tower: TowerWithCoords) => void
  getTowerIcon: (angle: number) => Promise<google.maps.Icon | null>
  isSelected?: boolean // Add this prop to handle selected state
}

export const TowerMarker: React.FC<TowerMarkerProps> = ({
  tower,
  angle,
  currentZoom,
  onMouseOver,
  onMouseOut,
  onClick,
  getTowerIcon,
  isSelected = false, // Default to false
}) => {
  const [icon, setIcon] = useState<google.maps.Icon | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const prevAngleRef = useRef<number>(angle)
  const prevZoomRef = useRef<number>(currentZoom)
  const isMountedRef = useRef<boolean>(true)

  useEffect(() => {
    // Set up the mounted ref
    isMountedRef.current = true

    const loadIcon = async () => {
      // Only load a new icon if the angle has changed, zoom has changed, or we don't have an icon yet
      if (prevAngleRef.current !== angle || prevZoomRef.current !== currentZoom || !icon) {
        try {
          setIsLoading(true)
          debugLog(`Loading icon for tower ${tower.name} with angle ${angle} at zoom ${currentZoom}`)

          const newIcon = await getTowerIcon(angle)

          if (isMountedRef.current) {
            if (newIcon) {
              debugLog(`Successfully loaded icon for tower ${tower.name}`)

              // If the tower is selected, modify the icon to show it's selected
              if (isSelected && newIcon.scaledSize) {
                // Create a slightly larger icon for selected towers
                const size = (newIcon.scaledSize as google.maps.Size).width
                const selectedSize = Math.round(size * 1.2) // 20% larger

                const selectedIcon = {
                  ...newIcon,
                  scaledSize: new window.google.maps.Size(selectedSize, selectedSize),
                  anchor: new window.google.maps.Point(selectedSize / 2, selectedSize / 2),
                  // Add a slight glow or highlight effect if possible
                  // This depends on the icon being used
                }

                setIcon(selectedIcon)
              } else {
                setIcon(newIcon)
              }

              prevAngleRef.current = angle
              prevZoomRef.current = currentZoom
              setError(null)
            } else {
              console.error(`TowerMarker: Failed to get icon for tower ${tower.name}`)
              setError("Failed to load icon")
            }
            setIsLoading(false)
          }
        } catch (err) {
          if (isMountedRef.current) {
            console.error(`TowerMarker: Error loading icon for tower ${tower.name}:`, err)
            setError(err instanceof Error ? err.message : "Unknown error")
            setIsLoading(false)
          }
        }
      }
    }

    loadIcon()

    return () => {
      isMountedRef.current = false
    }
  }, [angle, currentZoom, getTowerIcon, tower.name, icon, isSelected])

  const handleClick = () => {
    debugLog(`Tower marker clicked: ${tower.name}`)
    onClick(tower)
  }

  // Use a default marker if we're still loading or had an error
  if (isLoading || error || !icon) {
    return (
      <Marker
        position={{ lat: tower.lat, lng: tower.lng }}
        title={tower.name}
        onMouseOver={() => onMouseOver(tower)}
        onMouseOut={onMouseOut}
        onClick={handleClick}
        // Remove animation for selected state
        zIndex={isSelected ? 1000 : undefined} // Add zIndex instead to bring selected markers to front
      />
    )
  }

  return (
    <Marker
      position={{ lat: tower.lat, lng: tower.lng }}
      title={tower.name}
      icon={icon}
      onMouseOver={() => onMouseOver(tower)}
      onMouseOut={onMouseOut}
      onClick={handleClick}
      // Remove animation but keep zIndex for selected state
      zIndex={isSelected ? 1000 : undefined} // Bring selected markers to the front
    />
  )
}

